/**
 * Use Case: Atualizar Lead
 * Orquestra a atualização de um lead existente
 */

import { ILeadRepository } from '../../../domain/repositories/ILeadRepository';
import { Lead } from '../../../domain/entities/Lead';
import { NotFoundError } from '../../../core/errors/DomainErrors';
import { syncFollowUpsAfterLeadChange } from '../../../services/modules/followUpService';
import { sendStageMetaConversionIfConfigured } from '../../../services/modules/meta/metaStageConversionService';

export interface UpdateLeadDTO {
    id: string;
    name?: string;
    email?: string;
    whatsApp?: string;
    value?: number;
    stage?: string;
    status?: 'open' | 'won' | 'lost';
    origin?: string; // Aceita qualquer origem cadastrada pelo usuário
    agent?: string | null;
    tags?: string[];
    observations?: string;
    contactId?: string;
    /** Multi-tenant — preferível para CAPI de etapa */
    organizationId?: string;
}

export class UpdateLeadUseCase {
    constructor(private readonly leadRepository: ILeadRepository) { }

    async execute(dto: UpdateLeadDTO): Promise<void> {
        const existingLead = await this.leadRepository.findById(dto.id);

        if (!existingLead) {
            throw new NotFoundError('Lead', dto.id);
        }

        const previousStage = existingLead.stage;

        if (dto.name !== undefined) {
            existingLead.updateName(dto.name);
        }

        if (dto.email !== undefined) {
            existingLead.updateEmail(dto.email);
        }

        if (dto.whatsApp !== undefined) {
            existingLead.updateWhatsApp(dto.whatsApp);
        }

        if (dto.value !== undefined) {
            existingLead.updateValue(dto.value);
        }

        if (dto.agent !== undefined) {
            if (dto.agent) {
                existingLead.assignAgent(dto.agent);
            } else {
                existingLead.agent = null;
            }
        }

        if (dto.stage !== undefined) {
            if (dto.stage !== existingLead.stage) {
                existingLead.stageEntryDate = new Date();
            }
            existingLead.stage = dto.stage;
        }

        if (dto.status !== undefined) {
            existingLead.status = dto.status;
        }

        if (dto.origin !== undefined) {
            existingLead.origin = dto.origin;
        }

        if (dto.tags !== undefined) {
            existingLead.tags = dto.tags;
        }

        if (dto.observations !== undefined) {
            existingLead.observations = dto.observations;
        }

        if (dto.contactId !== undefined) {
            existingLead.contactId = dto.contactId;
        }

        await this.leadRepository.save(existingLead);

        if (dto.status !== undefined || dto.stage !== undefined) {
            syncFollowUpsAfterLeadChange(dto.id, {
                status: existingLead.status,
                stage: existingLead.stage,
            }).catch((err) =>
                console.warn('[UpdateLeadUseCase] Falha ao sincronizar follow-ups:', err?.name)
            );
        }

        // Marco Meta CAPI (async) — não bloqueia move; Purchase continua no ganho
        const stageChanged =
            dto.stage !== undefined &&
            dto.stage !== previousStage &&
            dto.stage !== 'won' &&
            dto.stage !== 'lost';
        const stillOpen = existingLead.status === 'open';
        if (stageChanged && stillOpen) {
            void sendStageMetaConversionIfConfigured(
                dto.id,
                dto.stage!,
                dto.organizationId,
            ).catch((err) =>
                console.warn('[UpdateLeadUseCase] Falha CAPI etapa', { name: err?.name })
            );
        }
    }
}
